import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    Activity,
    ArrowLeft,
    CalendarClock,
    Cloud,
    Cpu,
    ExternalLink,
    Eye,
    Hash,
    Loader2,
    Lock,
    Monitor,
    Network,
    PlayCircle,
    Rocket,
    Router,
    Send,
    Server,
    Shield,
    StopCircle,
    Trophy,
    Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@/components/ui/table';
import { FlagsSidebar } from '@/components/scenarios/FlagsSidebar';
import { InjectsSidebar } from '@/components/scenarios/InjectsSidebar';
import { ParticipantInjectsContent } from '@/components/scenarios/ParticipantInjectsContent';
import { ManageParticipantsDialog } from '@/components/scenarios/ManageParticipantsDialog';
import { ParticipantsSidebar } from '@/components/scenarios/ParticipantsSidebar';
import type { ExerciseRunParticipant, ExerciseRunResponse } from '@/lib/api/v2';
import { resolveMediaUrl } from '@/lib/resolveMediaUrl';
import { stripHtml } from '@/lib/utils';
import {
    useEndActiveScenarioMutation,
    useFinalizeExerciseRunParticipantsMutation,
    useGetExerciseActiveRunQuery,
    useLaunchExerciseActiveRunMutation,
    useSendActiveScenarioInjectMutation,
    useStartExerciseRunScenarioMutation,
} from '@/store/apiSlice';
import { canEndExerciseRun, isRunStatusStarted } from '@/lib/exerciseRunStatus';
import { useExerciseActiveRunWebSocket } from '@/hooks/useExerciseActiveRunWebSocket';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

function formatRunStatusLabel(status: string | undefined): string {
    if (!status) return 'Unknown';
    return status
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

/**
 * Obtained vs maximum: sum flag obtained_score vs sum of flag score caps;
 * plus inject obtained_score + bonus_points vs sum of inject points caps.
 */
function participantScoreObtainedOutOf(p: ExerciseRunParticipant): { obtained: number; outOf: number } {
    let obtained = 0;
    let outOf = 0;
    for (const f of p.flags ?? []) {
        const cap = typeof f.score === 'number' && Number.isFinite(f.score) ? f.score : 0;
        outOf += cap;
        const v = f.obtained_score;
        if (typeof v === 'number' && Number.isFinite(v)) obtained += v;
    }
    for (const inj of p.injects ?? []) {
        const cap = typeof inj.points === 'number' && Number.isFinite(inj.points) ? inj.points : 0;
        outOf += cap;
        const os = inj.obtained_score;
        const bp = inj.bonus_points;
        if (typeof os === 'number' && Number.isFinite(os)) obtained += os;
        if (typeof bp === 'number' && Number.isFinite(bp)) obtained += bp;
    }
    return { obtained, outOf };
}

function getRtkErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'data' in error) {
        const data = (error as { data?: unknown }).data;
        if (data && typeof data === 'object' && data !== null && 'detail' in data) {
            const d = (data as { detail?: unknown }).detail;
            if (typeof d === 'string') return d;
        }
    }
    if (error && typeof error === 'object' && 'error' in error) {
        const e = (error as { error?: unknown }).error;
        if (typeof e === 'string') return e;
    }
    if (error instanceof Error) return error.message;
    return 'Request failed';
}

export default function ExerciseRunSessionPage() {
    const { activeId } = useParams<{ activeId: string }>();
    const navigate = useNavigate();
    useExerciseActiveRunWebSocket(activeId);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [participantsSidebarOpen, setParticipantsSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'overview' | 'participants' | 'injects'>('overview');
    const [infraDetailsOpen, setInfraDetailsOpen] = useState(false);
    const prevDialogOpenRef = useRef(dialogOpen);

    const [startConfirmOpen, setStartConfirmOpen] = useState(false);
    const [confirmedAllParticipantsAdded, setConfirmedAllParticipantsAdded] = useState(false);
    const [pollWhileLaunching, setPollWhileLaunching] = useState(false);

    const {
        data: run,
        isLoading: runQueryLoading,
        isError: runQueryError,
        error: runQueryErr,
        refetch,
    } = useGetExerciseActiveRunQuery(activeId!, {
        skip: !activeId,
        // Launch completion used to arrive only over the websocket. Poll while
        // the run is launching so the page leaves "Provisioning" once Nova is done.
        pollingInterval: pollWhileLaunching ? 8000 : 0,
    });

    useEffect(() => {
        setPollWhileLaunching(run?.status === 'launching');
    }, [run?.status]);

    const [launchRun, { isLoading: launching }] = useLaunchExerciseActiveRunMutation();
    const [startScenarioMut, { isLoading: startingScenario }] = useStartExerciseRunScenarioMutation();
    const [finalizeParticipantsMut, { isLoading: markingReady }] = useFinalizeExerciseRunParticipantsMutation();
    const [endScenarioMut, { isLoading: endingScenario }] = useEndActiveScenarioMutation();
    const [sendInjectMut] = useSendActiveScenarioInjectMutation();

    const [showFlagsSidebar, setShowFlagsSidebar] = useState(false);
    const [selectedFlagsParticipant, setSelectedFlagsParticipant] = useState<any>(null);

    const [showInjectsSidebar, setShowInjectsSidebar] = useState(false);
    const [injectsSidebarTitle, setInjectsSidebarTitle] = useState<string>('');
    const [injectsSidebarContent, setInjectsSidebarContent] = useState<any>(null);

    const [sendingInjectId, setSendingInjectId] = useState<number | null>(null);

    const loadError = useMemo(() => {
        if (!runQueryError || !runQueryErr) return null;
        return getRtkErrorMessage(runQueryErr);
    }, [runQueryError, runQueryErr]);

    const loadingRun = Boolean(activeId && runQueryLoading && !run);

    const scenario = run?.scenario ?? null;
    const scenarioLoading = loadingRun && !scenario;

    const infraSummary = scenario?.infrastructure ?? null;
    const infraLoading = scenarioLoading;
    const infraFetching = false;

    const handleCloseFlagsSidebar = () => {
        setShowFlagsSidebar(false);
        setSelectedFlagsParticipant(null);
    };

    const handleCloseInjectsSidebar = () => {
        setShowInjectsSidebar(false);
        setInjectsSidebarContent(null);
        setInjectsSidebarTitle('');
    };

    const handleOpenFlagsSidebarForRunParticipant = async (p: ExerciseRunResponse['participants'][number]) => {
        const flags = Array.isArray((p as any).flags) ? (p as any).flags : [];
        const participantView: {
            full_name: string;
            team: string;
            avatar: string;
            flags: any[];
        } = {
            full_name: p.user?.full_name ?? p.user_full_name ?? `User ${p.user_id}`,
            team: p.team?.name ?? p.role_name ?? 'N/A',
            avatar: '',
            flags,
        };

        if (!(participantView as any).flags?.length) {
            toast.info('No flags mapped for this participant.');
            return;
        }

        if (showInjectsSidebar) handleCloseInjectsSidebar();
        setSelectedFlagsParticipant(participantView);
        setShowFlagsSidebar(true);
    };

    const handleOpenInjectsSidebarForRunParticipant = async (p: ExerciseRunResponse['participants'][number]) => {
        if (!p.has_ttx_access) return;
        const participantInjects = Array.isArray((p as any).injects) ? (p as any).injects : [];

        if (showFlagsSidebar) handleCloseFlagsSidebar();
        setInjectsSidebarTitle(p.user?.full_name || p.user_full_name || p.user?.email || p.user_email || 'Participant');
        setInjectsSidebarContent(
            <ParticipantInjectsContent
                injects={participantInjects as any}
                participantName={p.user?.full_name || p.user_full_name || undefined}
                activeScenarioId={activeId}
                onAfterEvaluate={() => {
                    void refetch();
                }}
            />,
        );
        setShowInjectsSidebar(true);
    };

    const participantCount = useMemo(() => run?.participants?.length ?? 0, [run]);
    /** Matches backend inject send: launched + started or paused. */
    const canSendTtxInjects = useMemo(
        () =>
            Boolean(
                run?.is_launched &&
                (run.status === 'started' || run.status === 'paused'),
            ),
        [run?.is_launched, run?.status],
    );
    /** Queue infra provisioning (participants finalized → launching). Retry allowed after failed. */
    const canLaunchScenario = Boolean(
        run &&
        (run.status === 'participants_added' || run.status === 'failed') &&
        run.is_launched !== true &&
        participantCount > 0 &&
        !['completed', 'cancelled'].includes(run.status),
    );
    /** Begin play after OpenStack / launch task completes (`launched`). */
    const canStartScenarioAfterLaunch = Boolean(
        run &&
        run.status === 'launched' &&
        run.is_launched === true &&
        !['completed', 'cancelled', 'failed'].includes(run.status),
    );

    useEffect(() => {
        if (!scenario?.ttx_enabled && activeTab === 'injects') {
            setActiveTab('overview');
        }
    }, [scenario?.ttx_enabled, activeTab]);

    // If user updates participants in the dialog, refresh so the page immediately shows them.
    useEffect(() => {
        const prev = prevDialogOpenRef.current;
        prevDialogOpenRef.current = dialogOpen;

        // Only refresh after a real close transition: true -> false.
        if (!prev && !dialogOpen) return;
        if (prev && !dialogOpen) {
            if (!activeId) return;
            void refetch();
        }
    }, [dialogOpen, activeId, refetch]);

    const handleLaunchExercise = async () => {
        if (!run || run.exercise_id == null || !activeId) return;
        try {
            await launchRun({
                exerciseId: String(run.exercise_id),
                runId: run.id,
                activeId,
            }).unwrap();
            toast.success('Launching scenario…');
        } catch (e) {
            toast.error(getRtkErrorMessage(e));
        }
    };

    const handleStartScenario = async () => {
        if (!run || !activeId) return;
        try {
            await startScenarioMut({ runId: run.id, activeId }).unwrap();
            toast.success('Scenario started');
        } catch (e) {
            toast.error(getRtkErrorMessage(e));
        }
    };

    const handleEndScenario = async () => {
        if (!activeId) return;
        try {
            await endScenarioMut({ activeId }).unwrap();
            toast.success('Scenario ended');
            navigate('/scenarios/actives');
        } catch (e: unknown) {
            toast.error(getRtkErrorMessage(e));
        }
    };

    const handleSendInject = async (injectId: number) => {
        if (!activeId) return;
        setSendingInjectId(injectId);
        try {
            await sendInjectMut({ activeId, injectId }).unwrap();
            toast.success('Inject sent to matching participants');
        } catch (e: unknown) {
            toast.error(getRtkErrorMessage(e));
        } finally {
            setSendingInjectId(null);
        }
    };

    const handleStartScenarioConfirmed = async (): Promise<boolean> => {
        if (!run || !activeId) return false;
        if (!confirmedAllParticipantsAdded) {
            toast.error('Please confirm that all participants have been added.');
            return false;
        }
        if (participantCount === 0) {
            toast.error('Please add participants before starting.');
            return false;
        }

        try {
            await finalizeParticipantsMut({ runId: run.id, activeId }).unwrap();
            toast.success('Participants finalized');
            return true;
        } catch (e: unknown) {
            toast.error(getRtkErrorMessage(e));
            return false;
        }
    };

    if (!activeId) {
        return (
            <div className="space-y-4 p-6">
                <p className="text-muted-foreground">Missing active id.</p>
                <Button variant="ghost" asChild>
                    <Link to="/scenarios/actives">Back to Active scenarios</Link>
                </Button>
            </div>
        );
    }

    if (loadingRun && !run) {
        return (
            <div className="mx-auto flex max-w-lg flex-col items-center justify-center gap-4 px-4 py-24">
                <div className="rounded-2xl border bg-card p-8 shadow-sm">
                    <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
                    <p className="mt-4 text-center text-sm font-medium text-foreground">Loading exercise run</p>
                    <p className="mt-1 text-center text-xs text-muted-foreground">Fetching participants and scenario details…</p>
                </div>
            </div>
        );
    }

    if (loadError || !run || run.exercise_id == null) {
        return (
            <div className="mx-auto max-w-lg space-y-4 px-4 py-10">
                <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center shadow-sm">
                    <p className="font-semibold text-destructive">{loadError ?? 'Active run not found.'}</p>
                    <p className="mt-2 text-sm text-muted-foreground">
                        The run may have ended or the link is invalid.
                    </p>
                </div>
                <Button variant="outline" className="w-full sm:w-auto" asChild>
                    <Link to="/scenarios/actives" className="gap-2">
                        <ArrowLeft className="h-4 w-4" />
                        Back to active scenarios
                    </Link>
                </Button>
            </div>
        );
    }

    const exerciseId = String(run.exercise_id);
    const scenarioName = scenario?.name ?? 'Exercise';
    const statusLabel =
        run.status_display ?? run.status ?? (run.is_launched ? 'Launched' : 'Draft');

    const infraBusy = infraLoading || infraFetching;
    const nets = infraSummary?.networks ?? [];
    const routers = infraSummary?.routers ?? [];
    const instances = infraSummary?.instances ?? [];
    const instanceImages = instances
        .map((i) => ({ name: (i.name ?? '').trim(), image: (i.image ?? '').trim() }))
        .filter((i) => i.image);
    const uniqueInstanceImages = Array.from(new Set(instanceImages.map((i) => i.image)));
    const instanceImageSummary =
        uniqueInstanceImages.length === 0
            ? ''
            : uniqueInstanceImages.length === 1
                ? uniqueInstanceImages[0]
                : instanceImages
                    .map((i) => (i.name ? `${i.name} (${i.image})` : i.image))
                    .join(', ');
    const subnets = infraSummary?.subnets ?? [];
    const securityGroups = infraSummary?.security_groups ?? [];
    const infraReady = infraSummary?.is_ready ?? infraSummary?.is_active;

    const thumbRaw = scenario?.thumbnail_url?.trim();
    const thumb = thumbRaw ? resolveMediaUrl(thumbRaw) || thumbRaw : undefined;

    return (
        <div className="mx-auto  space-y-6 px-4 py-6 pb-12">
            <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                <div className="border-b bg-gradient-to-br from-muted/40 via-card to-card px-4 py-4 sm:px-6 sm:py-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                        <div className="min-w-0">
                            <Button variant="ghost" size="sm" className="-ml-2 h-8 text-muted-foreground hover:text-foreground" asChild>
                                <Link to="/scenarios/actives" className="gap-2">
                                    <ArrowLeft className="h-4 w-4" />
                                    Active scenarios
                                </Link>
                            </Button>
                            <nav className="mt-1 text-xs text-muted-foreground" aria-label="Breadcrumb">
                                <span className="text-foreground/80">Run</span>
                                <span className="mx-1.5 text-muted-foreground/60">/</span>
                                <span className="font-mono text-[11px]">#{run.id}</span>
                            </nav>
                        </div>
                        <div className="flex flex-shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() => {
                                    setActiveTab('participants');
                                    const pCount = run?.participants?.length ?? 0;
                                    if (run?.status !== 'participants_pending') {
                                        setParticipantsSidebarOpen(true);
                                        return;
                                    }
                                    if (pCount === 0) setDialogOpen(true);
                                    else setParticipantsSidebarOpen(true);
                                }}
                            >
                                <Users className="h-4 w-4" />
                                Configure participants
                            </Button>
                            {run?.status === 'participants_pending' && participantCount > 0 && (
                                <Button
                                    type="button"
                                    size="sm"
                                    className="gap-2"
                                    onClick={() => {
                                        setConfirmedAllParticipantsAdded(false);
                                        setStartConfirmOpen(true);
                                    }}
                                    disabled={markingReady || launching || startingScenario}
                                >
                                    <Lock className="h-4 w-4" />
                                    {markingReady ? 'Marking…' : 'Finalize participants'}
                                </Button>
                            )}
                            {canLaunchScenario && (
                                <Button
                                    type="button"
                                    onClick={() => void handleLaunchExercise()}
                                    disabled={launching || startingScenario}
                                    className="gap-2 shadow-sm"
                                    size="sm"
                                >
                                    {launching ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Launching…
                                        </>
                                    ) : (
                                        <>
                                            <Rocket className="h-4 w-4" />
                                            Launch scenario
                                        </>
                                    )}
                                </Button>
                            )}
                            {canStartScenarioAfterLaunch && (
                                <Button
                                    type="button"
                                    onClick={() => void handleStartScenario()}
                                    disabled={startingScenario || launching}
                                    size="sm"
                                    className="gap-2 bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-600 disabled:opacity-60 dark:bg-green-600 dark:hover:bg-green-700"
                                >
                                    {startingScenario ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Starting…
                                        </>
                                    ) : (
                                        <>
                                            <PlayCircle className="h-4 w-4" />
                                            Start scenario
                                        </>
                                    )}
                                </Button>
                            )}
                            {run && isRunStatusStarted(run.status) && activeId && (
                                <Button
                                    size="sm"
                                    className="gap-2 bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500/40 dark:bg-blue-600 dark:hover:bg-blue-700"
                                    asChild
                                >
                                    <Link
                                        to={
                                            (() => {
                                                const rpId = run.participants?.[0]?.id;
                                                return rpId != null
                                                    ? `/scenarios/active/${activeId}/console?participant=${rpId}`
                                                    : `/scenarios/active/${activeId}/console`;
                                            })()
                                        }
                                    >
                                        <Monitor className="h-4 w-4" />
                                        Open console
                                    </Link>
                                </Button>
                            )}
                            {run && canEndExerciseRun(run.status) && (
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="destructive"
                                            size="sm"
                                            className="gap-2"
                                            disabled={endingScenario}
                                        >
                                            {endingScenario ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    Ending…
                                                </>
                                            ) : (
                                                <>
                                                    <StopCircle className="h-4 w-4" />
                                                    End scenario
                                                </>
                                            )}
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>End this scenario?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This will end &quot;{scenarioName}&quot; for all participants. This action cannot be undone.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel disabled={endingScenario}>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={() => void handleEndScenario()}
                                                disabled={endingScenario}
                                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                            >
                                                {endingScenario ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        Ending…
                                                    </>
                                                ) : (
                                                    'End scenario'
                                                )}
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            )}
                        </div>
                    </div>

                    <div className="mt-4 flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
                        <div
                            className={`relative shrink-0 overflow-hidden rounded-xl border bg-muted ${thumb ? '' : 'border-dashed'}`}
                        >
                            {thumb ? (
                                <img
                                    src={thumb}
                                    alt={scenarioName}
                                    className="h-36 w-full object-cover sm:h-40 lg:h-full lg:min-h-[11rem] lg:w-44"
                                />
                            ) : (
                                <div className="flex h-36 w-full items-center justify-center sm:h-40 lg:h-44 lg:w-44">
                                    <PlayCircle className="h-14 w-14 text-muted-foreground/35" aria-hidden />
                                </div>
                            )}
                        </div>

                        <div className="min-w-0 flex-1 space-y-3">
                            <div className="flex flex-wrap items-center gap-2">
                                {isRunStatusStarted(run.status) && (
                                    <span
                                        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-semibold text-white shadow-sm"
                                        title="Exercise is in progress"
                                    >
                                        <span className="relative flex h-2 w-2">
                                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                                            <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                                        </span>
                                        Live
                                    </span>
                                )}
                                <Badge variant="secondary" className="font-medium">
                                    {statusLabel}
                                </Badge>
                                {run.is_launched ? (
                                    <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary">
                                        Launched
                                    </Badge>
                                ) : null}
                            </div>
                            <div>
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                                    <h1 className="min-w-0 text-xl font-bold tracking-tight sm:text-2xl">{scenarioName}</h1>
                                    <Button variant="outline" size="sm" className="w-fit shrink-0 gap-1.5" asChild>
                                        <Link to={`/scenarios/${exerciseId}/detail`}>
                                            <ExternalLink className="h-3.5 w-3.5" />
                                            Full detail
                                        </Link>
                                    </Button>
                                </div>
                                {run.name && run.name !== scenarioName ? (
                                    <p className="mt-1 text-xs text-muted-foreground">Run name: {run.name}</p>
                                ) : null}
                                <p className="mt-2 text-xs text-muted-foreground">
                                    {formatRunStatusLabel(run.status)}
                                    {run.launched_at ? (
                                        <>
                                            {' · '}
                                            Launched {new Date(run.launched_at).toLocaleString()}
                                        </>
                                    ) : null}
                                </p>
                                {instanceImageSummary ? (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        {uniqueInstanceImages.length === 1 ? 'Image' : 'Images'}: {instanceImageSummary}
                                    </p>
                                ) : null}
                                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-border/70 pt-3 sm:grid-cols-4">
                                    <div>
                                        <div className="flex items-center gap-1.5 text-muted-foreground">
                                            <Users className="h-3.5 w-3.5 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
                                            <p className="text-[10px] font-medium uppercase tracking-wide">Participants</p>
                                        </div>
                                        <p className="mt-0.5 text-lg font-semibold tabular-nums text-foreground">
                                            {participantCount}
                                        </p>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-1.5 text-muted-foreground">
                                            <Network className="h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-violet-400" aria-hidden />
                                            <p className="text-[10px] font-medium uppercase tracking-wide">Networks</p>
                                        </div>
                                        <p className="mt-0.5 text-lg font-semibold tabular-nums text-foreground">
                                            {nets.length}
                                        </p>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-1.5 text-muted-foreground">
                                            <Server className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                                            <p className="text-[10px] font-medium uppercase tracking-wide">VMs</p>
                                        </div>
                                        <p className="mt-0.5 text-lg font-semibold tabular-nums text-foreground">
                                            {instances.length}
                                        </p>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-1.5 text-muted-foreground">
                                            <Trophy className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                                            <p className="text-[10px] font-medium uppercase tracking-wide">Points</p>
                                        </div>
                                        <p className="mt-0.5 text-lg font-semibold tabular-nums text-foreground">
                                            {scenario?.points ?? 0}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {run?.status === 'launching' && (
                <div
                    role="status"
                    className="flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-950 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-50"
                >
                    <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-amber-600 dark:text-amber-400" />
                    <div>
                        <p className="font-medium">Provisioning infrastructure</p>
                        <p className="mt-0.5 text-sm opacity-90">
                            Networks, routers, and VMs are being created. This may take several minutes—safe to leave this page open.
                        </p>
                    </div>
                </div>
            )}

            {run?.status === 'failed' && run.failure_detail?.trim() ? (
                <div
                    role="alert"
                    className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-destructive"
                >
                    <div>
                        <p className="font-medium">Launch failed</p>
                        <p className="mt-0.5 text-sm opacity-90">{run.failure_detail.trim()}</p>
                        {canLaunchScenario ? (
                            <p className="mt-2 text-sm opacity-90">Use Launch scenario to try again.</p>
                        ) : null}
                    </div>
                </div>
            ) : null}

            <Tabs
                value={activeTab}
                onValueChange={(v) => setActiveTab(v as 'overview' | 'participants' | 'injects')}
                className="space-y-6"
            >
                <TabsList className="h-auto w-full flex-wrap justify-start gap-1 rounded-xl border bg-muted/40 p-1 sm:w-auto">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="participants">Participants</TabsTrigger>
                    {scenario?.ttx_enabled && <TabsTrigger value="injects">Injects</TabsTrigger>}
                </TabsList>

                <TabsContent value="overview" className="space-y-4 outline-none">
                    <Card className="shadow-sm">
                        <CardHeader className="pb-2">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <CardTitle className="text-base">Run details</CardTitle>
                                    <CardDescription className="mt-1">
                                        Identity, lifecycle state, and roster size for this active run.
                                    </CardDescription>
                                </div>
                                <Badge
                                    variant={run?.is_launched ? 'default' : 'secondary'}
                                    className="w-fit shrink-0 text-xs font-medium"
                                >
                                    {statusLabel}
                                </Badge>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-2">
                            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                <div className="flex gap-3 rounded-xl border border-border/80 bg-muted/25 px-3 py-3">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-background shadow-sm ring-1 ring-border/60">
                                        <Hash className="h-4 w-4 text-sky-600 dark:text-sky-400" aria-hidden />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                            Run ID
                                        </p>
                                        <p className="mt-0.5 font-mono text-sm font-semibold tabular-nums text-foreground">
                                            {run.id}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-3 rounded-xl border border-border/80 bg-muted/25 px-3 py-3">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-background shadow-sm ring-1 ring-border/60">
                                        <Activity className="h-4 w-4 text-violet-600 dark:text-violet-400" aria-hidden />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                            Lifecycle
                                        </p>
                                        <p className="mt-0.5 text-sm font-semibold text-foreground">
                                            {formatRunStatusLabel(run.current_status ?? run.status)}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-3 rounded-xl border border-border/80 bg-muted/25 px-3 py-3">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-background shadow-sm ring-1 ring-border/60">
                                        <Rocket className="h-4 w-4 text-orange-600 dark:text-orange-400" aria-hidden />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                            Infra deployed
                                        </p>
                                        <div className="mt-1">
                                            {run.is_launched ? (
                                                <span className="inline-flex items-center rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                                                    Yes
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                                                    Not yet
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {run.launched_at ? (
                                    <div className="flex gap-3 rounded-xl border border-border/80 bg-muted/25 px-3 py-3 sm:col-span-2 xl:col-span-1">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-background shadow-sm ring-1 ring-border/60">
                                            <CalendarClock className="h-4 w-4 text-amber-600 dark:text-amber-400" aria-hidden />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                Launched at
                                            </p>
                                            <p className="mt-0.5 text-sm font-medium leading-snug text-foreground">
                                                {new Date(run.launched_at).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                ) : null}
                                <div className="flex gap-3 rounded-xl border border-border/80 bg-muted/25 px-3 py-3">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-background shadow-sm ring-1 ring-border/60">
                                        <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" aria-hidden />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                            Participants
                                        </p>
                                        <p className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">
                                            {participantCount}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="shadow-sm">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <Cloud className="h-5 w-5 text-muted-foreground" />
                                    <CardTitle className="text-base">Infrastructure</CardTitle>
                                </div>
                                {infraBusy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                                {!infraBusy && infraSummary && (
                                    <Badge variant={infraReady ? 'default' : 'secondary'}>
                                        {infraReady ? 'Ready' : 'Not ready'}
                                    </Badge>
                                )}
                            </div>
                            <CardDescription>
                                {infraSummary?.cloud_name
                                    ? `Cloud: ${infraSummary.cloud_name}`
                                    : 'Networks, routers, and VMs for this exercise'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {!infraSummary && infraBusy && (
                                <p className="text-muted-foreground text-sm">Loading infrastructure…</p>
                            )}
                            {!infraSummary && !infraBusy && (
                                <p className="text-muted-foreground text-sm">Infrastructure summary not loaded.</p>
                            )}
                            {infraSummary && (
                                <>
                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                                        <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
                                            <Network className="h-4 w-4 shrink-0 text-sky-600" />
                                            <div>
                                                <p className="text-muted-foreground text-xs">Networks</p>
                                                <p className="font-semibold">{nets.length}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
                                            <Cpu className="h-4 w-4 shrink-0 text-violet-600" />
                                            <div>
                                                <p className="text-muted-foreground text-xs">Subnets</p>
                                                <p className="font-semibold">{subnets.length}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
                                            <Router className="h-4 w-4 shrink-0 text-orange-600" />
                                            <div>
                                                <p className="text-muted-foreground text-xs">Routers</p>
                                                <p className="font-semibold">{routers.length}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
                                            <Server className="h-4 w-4 shrink-0 text-emerald-600" />
                                            <div>
                                                <p className="text-muted-foreground text-xs">VMs</p>
                                                <p className="font-semibold">{instances.length}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
                                            <Shield className="h-4 w-4 shrink-0 text-amber-600" />
                                            <div>
                                                <p className="text-muted-foreground text-xs">Sec. groups</p>
                                                <p className="font-semibold">{securityGroups.length}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between gap-3 pt-1">
                                        <p className="text-muted-foreground text-xs">
                                            {nets.length + routers.length + instances.length > 0
                                                ? 'Details available.'
                                                : 'No networks/routers/VMs found.'}
                                        </p>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setInfraDetailsOpen((v) => !v)}
                                        >
                                            {infraDetailsOpen ? 'Hide details' : 'Show details'}
                                        </Button>
                                    </div>

                                    {infraDetailsOpen && (
                                        (nets.length > 0 || routers.length > 0 || instances.length > 0) && (
                                            <div className="grid gap-4 md:grid-cols-3">
                                                {nets.length > 0 && (
                                                    <div>
                                                        <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wide">
                                                            Networks
                                                        </p>
                                                        <ul className="max-h-32 space-y-1 overflow-y-auto text-sm">
                                                            {nets.map((n) => (
                                                                <li key={n.id} className="truncate">
                                                                    {n.name}
                                                                    {n.lifecycle_state ? (
                                                                        <span className="text-muted-foreground"> — {n.lifecycle_state}</span>
                                                                    ) : null}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {routers.length > 0 && (
                                                    <div>
                                                        <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wide">
                                                            Routers
                                                        </p>
                                                        <ul className="max-h-32 space-y-1 overflow-y-auto text-sm">
                                                            {routers.map((r) => (
                                                                <li key={r.id} className="truncate">
                                                                    {r.name}
                                                                    {r.lifecycle_state ? (
                                                                        <span className="text-muted-foreground"> — {r.lifecycle_state}</span>
                                                                    ) : null}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {instances.length > 0 && (
                                                    <div>
                                                        <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wide">
                                                            VMs / instances
                                                        </p>
                                                        <ul className="max-h-32 space-y-1 overflow-y-auto text-sm">
                                                            {instances.map((i) => (
                                                                <li key={i.id} className="truncate" title={[i.name, i.image, i.power_state, i.team_name].filter(Boolean).join(' · ')}>
                                                                    {i.name}
                                                                    {i.image ? (
                                                                        <span className="text-muted-foreground"> · {i.image}</span>
                                                                    ) : null}
                                                                    {/* {i.power_state ? (
                                                                        <span className="text-muted-foreground"> — {i.power_state}</span>
                                                                    ) : null} */}
                                                                    {i.team_name ? (
                                                                        <span className="text-muted-foreground"> ({i.team_name})</span>
                                                                    ) : null}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>

                </TabsContent>

                <TabsContent value="participants" className="outline-none">
                    <Card className="mt-0 shadow-sm">
                        <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-2">
                                    <div className="space-y-1">
                                        <CardTitle className="text-base">Participants</CardTitle>
                                        <p className="text-xs text-muted-foreground">{participantCount} total</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            if (run?.status !== 'participants_pending') {
                                                toast.info('Participants are locked after finalize participants.');
                                                return;
                                            }
                                            setDialogOpen(true);
                                        }}
                                        size="sm"
                                        className="gap-2"
                                        disabled={!run || run?.status === 'launching'}
                                    >
                                        Add
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="gap-2"
                                        onClick={() => setParticipantsSidebarOpen(true)}
                                        disabled={!run || run?.status === 'launching'}
                                    >
                                        Manage
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                            {run?.participants?.length > 0 ? (
                                <div className="overflow-hidden rounded-lg border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-muted/40 hover:bg-muted/40">
                                                <TableHead>SN.</TableHead>
                                                <TableHead>Player</TableHead>
                                                <TableHead>Team</TableHead>
                                                <TableHead>Access</TableHead>
                                                <TableHead>Flags</TableHead>
                                                <TableHead>Injects</TableHead>
                                                <TableHead className="text-right min-w-[7.5rem] whitespace-nowrap">
                                                    Score
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {run.participants.map((p, index) => {
                                                const accessBits = [p.has_vm_access ? 'VM' : null, p.has_ttx_access ? 'TTX' : null,]
                                                    .filter(Boolean)
                                                    .join(' · ');
                                                const scoreBreakdown = participantScoreObtainedOutOf(p);

                                                return (
                                                    <TableRow key={p.id} className="hover:bg-muted/30">
                                                        <TableCell>
                                                            <strong>#{index + 1}</strong>
                                                        </TableCell>
                                                        <TableCell className="font-medium">
                                                            <div className="flex flex-col">
                                                                <span className="truncate">
                                                                    {p.user?.full_name || `User #${p.user_id}`}
                                                                </span>
                                                                <span className="text-muted-foreground">
                                                                    {p.user?.email || ''}
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant="secondary" className="font-normal">
                                                                {p.team?.name}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex flex-col gap-2">
                                                                <span className="text-muted-foreground">
                                                                    {accessBits || '—'}
                                                                </span>
                                                                {isRunStatusStarted(run?.status) && p.has_vm_access && activeId ? (
                                                                    <Link
                                                                        to={`/scenarios/active/${activeId}/console?participant=${p.id}`}
                                                                        className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                                                                    >
                                                                        <Monitor className="h-4 w-4 shrink-0" aria-hidden />
                                                                        View console
                                                                    </Link>
                                                                ) : null}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            {(p?.has_vm_access) ? (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                        void handleOpenFlagsSidebarForRunParticipant(p)
                                                                    }
                                                                    className="text-blue-600 hover:text-blue-700 gap-1"
                                                                >
                                                                    <Eye className="h-4 w-4" />
                                                                    View Flags ({p.flags!.length})
                                                                </Button>
                                                            ) : (
                                                                <span className="text-xs text-muted-foreground">—</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {p?.has_ttx_access ? (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                        void handleOpenInjectsSidebarForRunParticipant(p)
                                                                    }
                                                                    className="text-blue-600 hover:text-blue-700 gap-1"
                                                                >
                                                                    <Eye className="h-4 w-4" />
                                                                    View Injects ({p.injects!.length})
                                                                </Button>
                                                            ) : (
                                                                <span className="text-xs text-muted-foreground">—</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-right tabular-nums">
                                                            <span
                                                                className="inline-flex items-center justify-end gap-1.5"
                                                                title="Points earned / maximum from flags and injects (inject bonus counts toward earned)"
                                                            >
                                                                <Trophy className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                                                                <span className="font-semibold text-foreground">
                                                                    {scoreBreakdown.obtained}
                                                                </span>
                                                                <span className="text-muted-foreground font-normal">
                                                                    {' '}/ {scoreBreakdown.outOf}
                                                                </span>
                                                            </span>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="space-y-3 py-6 text-center">
                                    <p className="text-sm text-muted-foreground">No participants yet.</p>
                                    <div className="flex items-center justify-center gap-2">
                                        <Button
                                            type="button"
                                            onClick={() => {
                                                if (run?.status !== 'participants_pending') {
                                                    toast.info('Participants are locked after finalize participants.');
                                                    return;
                                                }
                                                setDialogOpen(true);
                                            }}
                                            size="sm"
                                            disabled={!run || run?.status === 'launching'}
                                        >
                                            Add
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setParticipantsSidebarOpen(true)}
                                            disabled={!run || run?.status === 'launching'}
                                        >
                                            Manage
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {scenario?.ttx_enabled && (
                    <TabsContent value="injects" className="outline-none">
                        <Card className="mt-0 shadow-sm">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base">Injects</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {scenario?.injects?.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">
                                        Injects view for exercise runs will be added here.
                                    </p>
                                ) : (
                                    <div className="border border-border rounded-lg overflow-hidden bg-card">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="border-border hover:bg-muted/50">
                                                    <TableHead className="w-[50px] text-muted-foreground">#</TableHead>
                                                    <TableHead className="text-muted-foreground">Title</TableHead>
                                                    <TableHead className="text-muted-foreground">Severity</TableHead>
                                                    <TableHead className="text-muted-foreground">Type</TableHead>
                                                    <TableHead className="text-muted-foreground">Team</TableHead>
                                                    <TableHead className="text-muted-foreground">Points</TableHead>
                                                    <TableHead className="text-right text-muted-foreground">Send</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {(scenario.injects ?? []).map((inject) => (
                                                    <TableRow key={inject.id} className="border-border hover:bg-muted/30">
                                                        <TableCell className="font-medium text-xs text-muted-foreground">
                                                            #{inject.order}
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-medium">
                                                                    {inject.title}
                                                                </span>
                                                                {inject.description && (
                                                                    <span className="text-xs text-muted-foreground line-clamp-2">
                                                                        {stripHtml(inject.description)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant="outline" className="text-xs capitalize">
                                                                {inject.severity}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="text-xs text-muted-foreground">
                                                                {inject.inject_type?.name ?? '—'}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>
                                                            {inject.apply_to_all_team ? (
                                                                <Badge variant="secondary" className="text-xs font-normal">
                                                                    All teams
                                                                </Badge>
                                                            ) : inject.teams && inject.teams.length > 0 ? (
                                                                <div className="flex flex-wrap gap-1 max-w-[220px]">
                                                                    {inject.teams.map((t) => (
                                                                        <Badge
                                                                            key={t.id}
                                                                            variant="outline"
                                                                            className="text-xs font-normal"
                                                                        >
                                                                            {t.name}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <span className="text-xs text-muted-foreground">—</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="text-xs text-muted-foreground">
                                                                {inject.points ?? 0} pts{' '}
                                                                {inject.points_type && (
                                                                    <>
                                                                        ·{' '}
                                                                        {inject.points_type === 'manual' ? 'Manual' : inject.points_type === 'decay' ? 'Decay' : 'Default'}
                                                                    </>
                                                                )}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {canSendTtxInjects ? (
                                                                inject.sent_for_this_run ? (
                                                                    <Badge
                                                                        variant="secondary"
                                                                        className="text-xs font-normal border-transparent bg-green-600 text-white hover:bg-green-600"
                                                                    >
                                                                        Injected
                                                                    </Badge>
                                                                ) : (
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        className="gap-1.5 bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500/40"
                                                                        disabled={sendingInjectId === inject.id}
                                                                        onClick={() => void handleSendInject(inject.id)}
                                                                    >
                                                                        {sendingInjectId === inject.id ? (
                                                                            <>
                                                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                                                Sending…
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Send className="h-3.5 w-3.5" />
                                                                                Send now
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                )
                                                            ) : (
                                                                <span
                                                                    className="text-xs text-muted-foreground"
                                                                    title="Available when the run is launched and the exercise is started or paused."
                                                                >
                                                                    —
                                                                </span>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}


                            </CardContent>
                        </Card>
                    </TabsContent>
                )}

            </Tabs>

            <AlertDialog
                open={startConfirmOpen}
                onOpenChange={(open) => {
                    setStartConfirmOpen(open);
                    if (!open) setConfirmedAllParticipantsAdded(false);
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Lock className="h-5 w-5" />
                            Finalize Participants
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Confirm that all participants have been added. Once confirmed, participant adding will be locked and
                            the scenario can be launched.
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    <div className="flex items-start gap-3">
                        <input
                            id="confirmParticipantsAdded"
                            type="checkbox"
                            checked={confirmedAllParticipantsAdded}
                            onChange={(e) => setConfirmedAllParticipantsAdded(e.target.checked)}
                            className="mt-1"
                        />
                        <label htmlFor="confirmParticipantsAdded" className="text-sm">
                            I confirm all participants have been added.
                        </label>
                    </div>

                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={!confirmedAllParticipantsAdded || markingReady}
                            onClick={async (e) => {
                                // Keep the modal open on errors; close only when the API calls succeed.
                                e.preventDefault();
                                const ok = await handleStartScenarioConfirmed();
                                if (ok) {
                                    setStartConfirmOpen(false);
                                    setConfirmedAllParticipantsAdded(false);
                                }
                            }}
                        >
                            <div className="flex items-center gap-2">
                                <Lock className="h-4 w-4" />
                                Finalize Participants
                            </div>
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <ManageParticipantsDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                scenarioId={exerciseId}
                scenarioName={scenarioName}
                runId={run.id}
                exerciseTtxEnabled={scenario?.ttx_enabled}
                onRunUpdated={() => void refetch()}
            />

            <FlagsSidebar
                participant={selectedFlagsParticipant}
                isOpen={showFlagsSidebar}
                onClose={handleCloseFlagsSidebar}
            />

            <ParticipantsSidebar
                isOpen={participantsSidebarOpen}
                onClose={() => setParticipantsSidebarOpen(false)}
                participants={run?.participants ?? []}
                exerciseId={exerciseId}
                runId={run.id}
                onRefresh={async () => {
                    await refetch();
                }}
                instances={scenario?.infrastructure?.instances ?? []}
                participantsLocked={run?.status !== 'participants_pending'}
                onManageParticipants={() => {
                    if (run?.status !== 'participants_pending') {
                        toast.info('Participants are locked after finalize participants.');
                        return;
                    }
                    setDialogOpen(true);
                    setParticipantsSidebarOpen(false);
                }}
            />

            <InjectsSidebar
                isOpen={showInjectsSidebar}
                onClose={handleCloseInjectsSidebar}
                title={injectsSidebarTitle}
            >
                {injectsSidebarContent}
            </InjectsSidebar>
        </div>
    );
}
